import React, { useMemo, useState } from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { DropdownItem, DropdownMenu, DropdownToggle, Dropdown } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Error from '../../Error';
import { GetOrganizationsQuery } from '../../graphql';
import useMainContext from '../../../hooks/main-provider.jsx';
import { actorRanks } from '../../../../config/common';
import { useSession } from 'next-auth/react';
import { STATIC_ASSETS_URL } from '../../constants';

const Icons = {
  logo: `${STATIC_ASSETS_URL}/sfac_logo white.png`,
};

const GetOrganizations = gql`
  ${GetOrganizationsQuery}
`;

const OrganizationDropdownWrapper = styled.div`
  flex: 1;
  overflow: visible;
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 25px;
  height: 25px;
  flex-shrink: 0;
  transition: all 0.3s ease;
  margin-right: 12px;
`;

const StyledChevron = styled(FontAwesomeIcon)`
  font-size: 12px;
  margin-left: 8px;
`;

const StyledDropdownToggle = styled(DropdownToggle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white !important;
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  width: 100%;
  text-align: left;

  &:hover {
    background: transparent !important;
  }

  &:focus,
  &:active {
    background: transparent !important;
    box-shadow: none !important;
  }
`;

const OrganizationName = styled.span`
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const StyledDropdownMenu = styled(DropdownMenu)`
  min-width: 220px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1050;
  position: absolute;
  margin-top: 10px;
`;

const DropdownHeader = styled.div`
  padding: 8px 16px;
  font-weight: 600;
  color: #6e6e6e;
  font-size: 16px;
  border-bottom: 2px solid #eee;
`;

const StyledDropdownItem = styled(DropdownItem)`
  padding: 10px 16px;
  cursor: pointer;
  color: #6e6e6e;
  font-size: 16px;
  font-weight: 300;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const SidebarOrganizations = ({ isCollapsed }) => {
  const { data: session } = useSession();
  const user = session?.user;
  const { data, loading, error } = useQuery(GetOrganizations, { skip: !user });
  const [isOpen, setIsOpen] = useState(false);

  const { selectedOrganization, updateSelectedOrganization } = useMainContext();

  const organizations = useMemo(() => {
    return (data && data.organizations) || [];
  }, [data]);

  const selectedOrgName = useMemo(() => {
    if (selectedOrganization) {
      const org = organizations.find(o => o.id === Number(selectedOrganization.id));
      return org ? org.displayName : 'Stress Free Auto Care';
    }
    return 'Stress Free Auto Care';
  }, [selectedOrganization, organizations]);

  if (error) {
    return <Error error={error} />;
  }

  const showDropdown = user?.rank <= actorRanks.organizationOwner && organizations.length > 1;

  if (!showDropdown) {
    return (
      <OrganizationDropdownWrapper>
        <LogoImage src={Icons.logo} alt="SFAC Logo" />
        {!isCollapsed && <OrganizationName>{selectedOrgName}</OrganizationName>}
      </OrganizationDropdownWrapper>
    );
  }

  return (
    <OrganizationDropdownWrapper>
      <Dropdown isOpen={isOpen} toggle={() => setIsOpen(!isOpen)}>
        <StyledDropdownToggle caret={false}>
          <LogoImage src={Icons.logo} alt="SFAC Logo" />
          {!isCollapsed && (
            <>
              <OrganizationName>{selectedOrgName}</OrganizationName>
              <StyledChevron icon={faChevronDown} />
            </>
          )}
        </StyledDropdownToggle>
        <StyledDropdownMenu>
          <DropdownHeader>Organizations</DropdownHeader>
          {organizations.map(org => (
            <StyledDropdownItem
              key={org.id}
              onClick={() => {
                updateSelectedOrganization(org.id);
                setIsOpen(false);
              }}
            >
              {org.displayName}
            </StyledDropdownItem>
          ))}
        </StyledDropdownMenu>
      </Dropdown>
    </OrganizationDropdownWrapper>
  );
};

export default SidebarOrganizations;
